/**
 * Scope Form Schema
 * 
 * This file defines the structure of the descriptive project scope form.
 * Each section contains fields with various types that the ScopeForm component
 * renders dynamically.
 * 
 * Supported field types:
 *   - "text input"
 *   - "number input"
 *   - "textarea"
 *   - "dropdown" (requires "options" array)
 *   - "multi-select tags" (requires "options" array)
 *   - "multi-select checkboxes" (requires "options" array)
 *   - "editable checklist" (requires "default_items" array)
 *   - Sections can have type: "repeatable block" or "repeatable table rows"
 */

// NOTE: Student and Mentor identity come from the project/student/mentor records,
// so they are intentionally NOT part of the scope schema. The scope document is
// mined only for the project plan, not for who is involved. Keep this in sync with
// backend/app/utils/scope_schema.py.
const scopeFormSchema = {
  sections: [
    {
      section: "Project Info",
      fields: [
        { name: "Project Title", type: "text input" },
        { name: "Project Summary", type: "textarea" }
      ]
    },
    {
      section: "Duration & Engagement",
      fields: [
        { name: "Total Duration", type: "dropdown", options: ["1 Month", "2 Months", "3 Months", "6 Months"] },
        { name: "Total Weeks", type: "number input" },
        { name: "Mentoring Frequency", type: "dropdown", options: ["Once a week", "Twice a week", "Three times a week", "On demand"] },
        { name: "Mentoring Format", type: "multi-select checkboxes", options: ["Live mentoring", "Hands-on guidance", "Design review", "Async review"] },
        { name: "Estimated Total Mentor Hours", type: "number input" }
      ]
    },
    {
      section: "Learning Objectives",
      fields: [
        {
          name: "Objectives",
          type: "editable checklist",
          note: "Add the learning objectives for this project",
          default_items: []
        }
      ]
    },
    {
      section: "Project Phases",
      type: "repeatable block",
      fields: [
        { name: "Phase Number", type: "number input" },
        { name: "Phase Name", type: "text input" },
        { name: "Duration (Week Range)", type: "text input", placeholder: "e.g. Week 1-2" },
        { name: "Scope", type: "textarea" },
        { name: "Deliverables", type: "textarea" }
      ]
    },
    {
      section: "Tools & Technologies",
      type: "repeatable table rows",
      fields: [
        { name: "Layer", type: "dropdown", options: ["Frontend", "Backend", "Database", "File Storage", "Auth", "Audio Capture", "API/AI", "Hosting", "Other"] },
        { name: "Technology", type: "text input" },
        { name: "Purpose in Project", type: "textarea" }
      ]
    },
    {
      section: "Outcome Checklist",
      fields: [
        {
          name: "End-to-End Flow Steps",
          type: "editable checklist",
          note: "Add the end-to-end steps that describe the project outcome",
          default_items: []
        }
      ]
    },
    {
      section: "Mentor's Role",
      fields: [
        {
          name: "Responsibilities",
          type: "multi-select checkboxes",
          options: [
            "Project scope and planning",
            "Weekly guided mentoring",
            "Design review and validation",
            "Engineering mindset development",
            "Safe experimentation guidance",
            "Final project evaluation and feedback"
          ]
        },
        { name: "Additional Notes", type: "textarea" }
      ]
    }
  ]
};

export default scopeFormSchema;
